import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Form, Input, Button, message, Row, Col, Typography, Alert, Checkbox } from 'antd';
import { ArrowRightOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import { registerProfile } from '../../auth';
import styles from './Auth.module.css';
import { api } from '../../api';
import { Helmet } from 'react-helmet';

const { Title } = Typography;

const inputStyle = {
  border: '2px solid #f5f5f5',
  backgroundColor: '#f5f5f5',
  padding: '8px',
  transition: 'all 0.3s',
};

const onMouseEnter = e => { e.currentTarget.style.backgroundColor = '#eee'; };
const onMouseLeave = e => { e.currentTarget.style.backgroundColor = '#f5f5f5'; };
const onFocus = e => { e.currentTarget.style.border = '2px solid #0072ff'; };
const onBlur = e => { e.currentTarget.style.border = '2px solid #f5f5f5'; };

const RegisterProfile = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const navigate = useNavigate();
  const { token } = useParams();
  const [emailConfirmed, setEmailConfirmed] = useState(false);
  const [tokenExpired, setTokenExpired] = useState(false);
  const [utmParams, setUtmParams] = useState({});
  const [step, setStep] = useState(1);

  useEffect(() => {
    const storedParams = JSON.parse(localStorage.getItem('utmParams') || '{}');
    setUtmParams(storedParams);
  }, []);

  useEffect(() => {
    if (!token) {
      navigate('/login', { replace: true });
      return;
    }
    if (token === 'preview') {
      setEmailConfirmed(true);
      setEmail('preview@example.com');
      return;
    }
    const confirmEmail = async () => {
      try {
        const response = await api.get(`/auth/confirm-email/${token}`);
        setEmailConfirmed(true);
        setEmail(response.data.email);
        message.success('メールアドレスの認証が完了しました。');
      } catch (error) {
        if (error.response && error.response.status === 400) {
          setEmailConfirmed(false);
          setTokenExpired(true);
          message.error('トークンが無効または期限切れです。再度登録をお試しください。');
          setTimeout(() => navigate('/login'), 3000);
        } else {
          message.error('無効なトークンです。再度登録をお試しください。');
          navigate('/login');
        }
      }
    };
    confirmEmail();
  }, [token, navigate]);

  const handleNextStep = async () => {
    try {
      await form.validateFields([
        ['full_name', 'last_name'],
        ['full_name', 'first_name'],
        ['full_name_kana', 'last_name_kana'],
        ['full_name_kana', 'first_name_kana'],
      ]);
      setStep(2);
    } catch {
      // validation errors shown by Form
    }
  };

  const handleRegisterProfile = async values => {
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('email', email);
      formData.append('last_name', values.full_name.last_name);
      formData.append('first_name', values.full_name.first_name);
      formData.append('last_name_kana', values.full_name_kana.last_name_kana);
      formData.append('first_name_kana', values.full_name_kana.first_name_kana);
      if (values.company_name) formData.append('company_name', values.company_name);
      if (values.department_name) formData.append('department_name', values.department_name);
      formData.append('password', values.password);
      formData.append('confirm_password', values.confirm_password);
      formData.append('agreed_to_terms', values.agreed_to_terms);
      formData.append('agreed_to_privacy_policy', values.agreed_to_privacy_policy);
      formData.append('token', token);
      formData.append('is_active', 'true');

      Object.entries(utmParams).forEach(([key, value]) => {
        formData.append(key, value);
      });

      // Meta 広告トラッキングパラメータを明示的に追加（localStorageに保存済みの値を使用）
      ['fbclid', 'meta_fbc', 'meta_fbp'].forEach(key => {
        if (utmParams[key]) formData.append(key, utmParams[key]);
      });

      await registerProfile(formData);

      localStorage.setItem(
        'tempLoginCredentials',
        JSON.stringify({ email: email, password: values.password }),
      );

      navigate('/register/thanks');
    } catch (error) {
      console.error('Registration error:', error);
      if (error.response && error.response.data && error.response.data.detail) {
        message.error(`会員登録に失敗しました: ${error.response.data.detail}`);
      } else {
        message.error('会員登録に失敗しました');
      }
    }
    setLoading(false);
  };

  const labelSpan = (text) => (
    <span style={{ color: '#888', fontSize: 12, fontWeight: 500 }}>{text}</span>
  );

  return (
    <>
      <Helmet>
        <title>新規ユーザー登録 | magicss</title>
        <meta name="robots" content="noindex" />
        <meta property="og:title" content="新規ユーザー登録 | magicss" />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={window.location.href} />
        <meta property="og:image" content={`${process.env.PUBLIC_URL}/img/ogp.png`} />
        <meta property="og:description" content="コンテンツ生成AIツール「magicss」の新規ユーザー登録ページです。" />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>

      <div className={styles.registerPage}>
        <div style={{
          position: 'relative', zIndex: 1,
          minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: '40px 20px',
        }}>
          <div style={{
            width: '100%', maxWidth: 520,
            background: 'rgba(255,255,255,0.95)',
            backdropFilter: 'blur(20px)',
            borderRadius: 16,
            padding: '40px 36px',
            boxShadow: '0 8px 40px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.06)',
          }}>
            <div className="logo outside" style={{ marginBottom: 8 }}>
              <span>Ignite creativity with..</span>
              magicss
            </div>

            {/* ミニプログレスインジケーター */}
            <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginBottom: 16 }}>
              <div style={{
                width: 32, height: 3, borderRadius: 2,
                background: '#0072ff', transition: 'all 0.3s',
              }} />
              <div style={{
                width: 32, height: 3, borderRadius: 2,
                background: step === 2 ? '#0072ff' : '#e0e0e0', transition: 'all 0.3s',
              }} />
            </div>

            <Title level={3} style={{ textAlign: 'center', marginBottom: 4, fontSize: 20, fontWeight: 600 }}>
              {step === 1 ? 'あと少しで完了です' : '最後のステップです'}
            </Title>
            <div style={{ textAlign: 'center', color: '#999', fontSize: 13, marginBottom: 28 }}>
              {step === 1 ? 'お名前と所属を入力してください' : 'パスワードを設定して登録を完了'}
            </div>

            {tokenExpired ? (
              <Alert
                message="リンクの有効期限が切れています"
                description="お手数ですが、再度登録をお試しください。"
                type="error"
                showIcon
                action={
                  <Button type="primary" size="small" onClick={() => navigate('/register')}>
                    登録ページへ
                  </Button>
                }
              />
            ) : emailConfirmed ? (
              <Form form={form} onFinish={handleRegisterProfile} layout="vertical" autoComplete="on" id="registerForm">
                {/* Chromeがメールアドレスをパスワードのユーザー名として認識するためのhiddenフィールド */}
                <input type="text" name="email" autoComplete="username" value={email} readOnly style={{ position: 'absolute', opacity: 0, height: 0, width: 0, overflow: 'hidden', pointerEvents: 'none' }} tabIndex={-1} />
                {/* スライドコンテナ */}
                <div style={{ overflow: 'hidden' }}>
                  <div style={{
                    display: 'flex', width: '200%',
                    transform: step === 1 ? 'translateX(0)' : 'translateX(-50%)',
                    transition: 'transform 0.35s ease',
                  }}>
                    {/* STEP 1: 氏名 + カナ + 会社情報 */}
                    <div style={{ width: '50%', flexShrink: 0, padding: '0 2px' }}>
                      <Row gutter={12}>
                        <Col xs={12}>
                          <Form.Item name={['full_name', 'last_name']} label={labelSpan('姓')}
                            rules={[{ required: true, message: '姓を入力' }]}>
                            <Input placeholder="山田" size="large" style={inputStyle} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave} onFocus={onFocus} onBlur={onBlur} />
                          </Form.Item>
                        </Col>
                        <Col xs={12}>
                          <Form.Item name={['full_name', 'first_name']} label={labelSpan('名')}
                            rules={[{ required: true, message: '名を入力' }]}>
                            <Input placeholder="太郎" size="large" style={inputStyle} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave} onFocus={onFocus} onBlur={onBlur} />
                          </Form.Item>
                        </Col>
                      </Row>
                      <Row gutter={12}>
                        <Col xs={12}>
                          <Form.Item name={['full_name_kana', 'last_name_kana']} label={labelSpan('姓（カナ）')}
                            rules={[{ required: true, message: 'カナを入力' }]}>
                            <Input placeholder="ヤマダ" size="large" style={inputStyle} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave} onFocus={onFocus} onBlur={onBlur} />
                          </Form.Item>
                        </Col>
                        <Col xs={12}>
                          <Form.Item name={['full_name_kana', 'first_name_kana']} label={labelSpan('名（カナ）')}
                            rules={[{ required: true, message: 'カナを入力' }]}>
                            <Input placeholder="タロウ" size="large" style={inputStyle} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave} onFocus={onFocus} onBlur={onBlur} />
                          </Form.Item>
                        </Col>
                      </Row>
                      <div style={{ borderTop: '1px solid #f0f0f0', margin: '4px 0 20px' }} />
                      <Row gutter={12}>
                        <Col xs={24} sm={12}>
                          <Form.Item name="company_name" label={labelSpan('会社名（任意）')}>
                            <Input placeholder="株式会社..." size="large" style={inputStyle} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave} onFocus={onFocus} onBlur={onBlur} />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                          <Form.Item name="department_name" label={labelSpan('部署名（任意）')}>
                            <Input placeholder="マーケティング部" size="large" style={inputStyle} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave} onFocus={onFocus} onBlur={onBlur} />
                          </Form.Item>
                        </Col>
                      </Row>

                      <Button type="primary" block size="large" onClick={handleNextStep}
                        style={{
                          height: 48, background: 'linear-gradient(90deg, #0072ff 0%, #00c6ff 100%)',
                          border: 'none', borderRadius: 8, fontWeight: 600, fontSize: 15, transition: 'all 0.3s',
                        }}
                        onMouseEnter={e => { e.currentTarget.style.opacity = '0.85'; }}
                        onMouseLeave={e => { e.currentTarget.style.opacity = '1.0'; }}>
                        次へ
                        <ArrowRightOutlined style={{ marginLeft: 8, fontSize: 13 }} />
                      </Button>
                    </div>

                    {/* STEP 2: パスワード + 同意 + 送信 */}
                    <div style={{ width: '50%', flexShrink: 0, padding: '0 2px' }}>
                      <div
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: '#0072ff', fontSize: 13, cursor: 'pointer', marginBottom: 20 }}
                        onClick={() => setStep(1)}>
                        <ArrowLeftOutlined style={{ fontSize: 11 }} />
                        戻る
                      </div>

                      <Row gutter={12}>
                        <Col xs={24} sm={12}>
                          <Form.Item name="password" label={labelSpan('パスワード')}
                            rules={[
                              { required: true, message: 'パスワードを入力' },
                              { min: 8, message: '8文字以上' },
                            ]}>
                            <div style={{ border: '2px solid #f5f5f5', backgroundColor: '#f5f5f5', transition: 'all 0.3s', borderRadius: 8 }}
                              onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#eee'; }}
                              onMouseLeave={e => { e.currentTarget.style.backgroundColor = '#f5f5f5'; }}
                              onFocus={e => { e.currentTarget.style.border = '2px solid #0072ff'; }}
                              onBlur={e => { e.currentTarget.style.border = '2px solid #f5f5f5'; }}>
                              <Input.Password placeholder="8文字以上" autoComplete="new-password" size="large"
                                style={{ border: 'none', backgroundColor: 'transparent', outline: 'none', padding: 8 }} />
                            </div>
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                          <Form.Item name="confirm_password" label={labelSpan('パスワード（確認）')}
                            dependencies={['password']}
                            rules={[
                              { required: true, message: '確認用を入力' },
                              ({ getFieldValue }) => ({
                                validator(_, value) {
                                  if (!value || getFieldValue('password') === value) return Promise.resolve();
                                  return Promise.reject(new Error('パスワードが一致しません'));
                                },
                              }),
                            ]}>
                            <div style={{ border: '2px solid #f5f5f5', backgroundColor: '#f5f5f5', transition: 'all 0.3s', borderRadius: 8 }}
                              onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#eee'; }}
                              onMouseLeave={e => { e.currentTarget.style.backgroundColor = '#f5f5f5'; }}
                              onFocus={e => { e.currentTarget.style.border = '2px solid #0072ff'; }}
                              onBlur={e => { e.currentTarget.style.border = '2px solid #f5f5f5'; }}>
                              <Input.Password placeholder="もう一度入力" autoComplete="new-password" size="large"
                                style={{ border: 'none', backgroundColor: 'transparent', outline: 'none', padding: 8 }} />
                            </div>
                          </Form.Item>
                        </Col>
                      </Row>

                      <div style={{ borderTop: '1px solid #f0f0f0', margin: '4px 0 20px' }} />
                      <Form.Item name="agreed_to_terms" valuePropName="checked" style={{ marginBottom: 8 }}
                        rules={[{ validator: (_, v) => v ? Promise.resolve() : Promise.reject(new Error('同意が必要です')) }]}>
                        <Checkbox>
                          <a href="/terms-of-service" target="_blank" rel="noopener noreferrer">利用規約</a>に同意する
                        </Checkbox>
                      </Form.Item>
                      <Form.Item name="agreed_to_privacy_policy" valuePropName="checked" style={{ marginBottom: 24 }}
                        rules={[{ validator: (_, v) => v ? Promise.resolve() : Promise.reject(new Error('同意が必要です')) }]}>
                        <Checkbox>
                          <a href="/privacy-policy" target="_blank" rel="noopener noreferrer">プライバシーポリシー</a>に同意する
                        </Checkbox>
                      </Form.Item>

                      <Form.Item style={{ marginBottom: 0 }}>
                        <Button type="primary" htmlType="submit" loading={loading} block size="large"
                          style={{
                            height: 48, background: 'linear-gradient(90deg, #0072ff 0%, #00c6ff 100%)',
                            border: 'none', borderRadius: 8, fontWeight: 600, fontSize: 15, transition: 'all 0.3s',
                          }}
                          onMouseEnter={e => { e.currentTarget.style.opacity = '0.85'; }}
                          onMouseLeave={e => { e.currentTarget.style.opacity = '1.0'; }}>
                          登録を完了する
                          <ArrowRightOutlined style={{ marginLeft: 8, fontSize: 13 }} />
                        </Button>
                      </Form.Item>
                    </div>
                  </div>
                </div>
              </Form>
            ) : (
              <Alert
                message="メールアドレスを確認しています..."
                description="しばらくお待ちください。"
                type="info"
                showIcon
              />
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default RegisterProfile;
